import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import logoWhite from "../assets/logowhite.svg";
import teleBirrLogo from "../assets/telebirrlogo.svg";
import cbeBirrLogo from "../assets/cbebirr.png";
import googlePlayLogo from "../assets/google_play.png";
import appStoreLogo from "../assets/app_store.png";
import CheckCircleIcon from "../components/icons/CheckCircle";
import ErrorIcon from "../components/icons/Error";
import LockIcon from "../components/icons/Lock";
import PhoneIcon from "../components/icons/Phone";
import {
  GUEST_PAY_API_READY,
  createGuestCheckout,
  fetchGuestInvoice,
  previewGuestInvoice,
} from "../api/guestPay";

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "—";
  return amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const GuestPay = () => {
  const { invoiceNumber } = useParams();
  const [searchParams] = useSearchParams();

  const [invoice, setInvoice] = useState(null);
  const [pageState, setPageState] = useState("loading");
  const [payError, setPayError] = useState("");
  const [isPaying, setIsPaying] = useState(false);
  const [copied, setCopied] = useState(false);

  const previewState = searchParams.get("preview");
  const usePreview =
    previewState !== null || !GUEST_PAY_API_READY;

  const loadInvoice = () => {
    if (!invoiceNumber) {
      setPageState("not_found");
      setInvoice(null);
      return;
    }

    if (usePreview) {
      const preview = previewGuestInvoice(invoiceNumber);
      if (previewState === "paid") preview.status = 1;
      if (previewState === "rejected") preview.status = 2;
      if (previewState === "notfound") {
        setInvoice(null);
        setPageState("not_found");
        return;
      }
      setInvoice(preview);
      setPageState(preview.status === 1 ? "paid" : "ready");
      return;
    }

    setPageState("loading");
    setPayError("");

    fetchGuestInvoice(invoiceNumber)
      .then((data) => {
        setInvoice(data);
        setPageState(Number(data.status) === 1 ? "paid" : "ready");
      })
      .catch((error) => {
        setInvoice(null);
        setPageState(error.code === "not_found" ? "not_found" : "error");
      });
  };

  useEffect(() => {
    document.title = invoiceNumber
      ? `Pay #${invoiceNumber} | iChereta`
      : "Pay Invoice | iChereta";
    loadInvoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNumber, previewState]);

  const statusMeta = useMemo(() => {
    const status = Number(invoice?.status);
    if (status === 1) {
      return { label: "ተከፍሏል · Paid", tone: "paid" };
    }
    if (status === 2) {
      return { label: "ውድቅ · Rejected", tone: "rejected" };
    }
    return { label: "ያልተከፈለ · Unpaid", tone: "unpaid" };
  }, [invoice]);

  const copyInvoiceNumber = async () => {
    if (!invoice?.invoice_number) return;
    try {
      await navigator.clipboard.writeText(String(invoice.invoice_number));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const handlePay = () => {
    if (!invoice?.invoice_number || isPaying) return;
    setIsPaying(true);
    setPayError("");

    createGuestCheckout(invoice.invoice_number)
      .then((result) => {
        window.location.href = result.url;
      })
      .catch((error) => {
        setIsPaying(false);
        if (error.code === "already_paid") {
          setInvoice(
            error.invoice
              ? {
                  ...invoice,
                  ...error.invoice,
                  status: 1,
                }
              : { ...invoice, status: 1 }
          );
          setPageState("paid");
          return;
        }
        setPayError(
          error.message ||
            "የክፍያ አገልግሎቱ አልጀመረም። እንደገና ይሞክሩ ወይም 0955305555 ይደውሉ።"
        );
      });
  };

  const billedTo =
    invoice?.company_name || invoice?.customer_name || "";

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center bg-[#F3F0FA]"
      style={{ fontFamily: "'Noto Sans Ethiopic', sans-serif" }}
    >
      <header className="w-full bg-[#664BC7] text-white flex flex-col items-center pt-6 pb-20 px-5">
        <img src={logoWhite} alt="iChereta" className="w-28 sm:w-32" />
        <h1 className="mt-5 text-[22px] sm:text-2xl font-bold tracking-tight">
          {pageState === "paid" ? "ክፍያ ተፈጽሟል" : "ክፍያ ይፈጽሙ"}
        </h1>
        <p className="mt-1 text-sm text-white/80">
          {pageState === "paid"
            ? "This invoice has already been paid"
            : "Pay your iChereta invoice"}
        </p>
      </header>

      <main className="w-[92%] max-w-[440px] -mt-[72px] flex flex-col flex-grow pb-8">
        {pageState === "loading" && <LoadingCard />}

        {pageState === "not_found" && (
          <StatusCard
            icon={<ErrorIcon styles="w-14 h-14 text-red-500" />}
            title="የክፍያ መጠየቂያ አልተገኘም"
            subtitle="This invoice could not be found. Check the link from your SMS and try again."
            invoiceNumber={invoiceNumber}
          />
        )}

        {pageState === "error" && (
          <StatusCard
            icon={<ErrorIcon styles="w-14 h-14 text-amber-500" />}
            title="መረጃው አልመጣም"
            subtitle="We couldn’t load this invoice. Check your connection and try again."
            invoiceNumber={invoiceNumber}
            actionLabel="እንደገና ሞክር · Retry"
            onAction={loadInvoice}
          />
        )}

        {pageState === "paid" && invoice && (
          <PaidInvoiceCard invoice={invoice} />
        )}

        {pageState === "ready" && invoice && (
          <>
            <section className="bg-white rounded-2xl shadow-[0_10px_30px_rgba(61,40,120,0.08)] px-5 pt-5 pb-6">
              <div className="flex items-center justify-between gap-3">
                <span
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                    statusMeta.tone === "rejected"
                      ? "bg-red-50 text-red-600"
                      : "bg-amber-50 text-amber-700"
                  }`}
                >
                  {statusMeta.label}
                </span>
                <button
                  type="button"
                  onClick={copyInvoiceNumber}
                  className="text-xs text-[#664BC7] font-medium"
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>

              <p className="mt-4 text-[11px] uppercase tracking-[0.16em] text-[#8A7BB5]">
                የክፍያ ቁጥር · Invoice
              </p>
              <button
                type="button"
                onClick={copyInvoiceNumber}
                className="mt-1 text-lg font-bold text-[#2D2156] tracking-wide text-left"
              >
                #{invoice.invoice_number}
              </button>

              <div className="mt-6">
                <p className="text-[11px] uppercase tracking-[0.16em] text-[#8A7BB5]">
                  የክፍያ መጠን · Amount
                </p>
                <div className="mt-1 flex items-end gap-2">
                  <span className="text-[40px] leading-none font-bold text-[#2D2156] tabular-nums">
                    {formatAmount(invoice.amount)}
                  </span>
                  <span className="mb-1 text-base font-semibold text-[#664BC7]">
                    ብር
                  </span>
                </div>
              </div>

              <dl className="mt-6 divide-y divide-[#EFEAF8]">
                {invoice.plan_name && (
                  <DetailRow
                    label="ጥቅል · Plan"
                    value={invoice.plan_name}
                  />
                )}
                {billedTo && (
                  <DetailRow label="ለ · Billed to" value={billedTo} />
                )}
                <DetailRow label="ተ.እ.ታ · VAT" value="15% included" />
              </dl>

              {statusMeta.tone === "rejected" && (
                <p className="mt-4 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
                  Previous payment was rejected. You can pay again below.
                </p>
              )}
            </section>

            <div className="sticky bottom-0 mt-5 -mx-[4%] px-[4%] pt-3 pb-[max(16px,env(safe-area-inset-bottom))] bg-gradient-to-t from-[#F3F0FA] via-[#F3F0FA] to-transparent">
              <button
                type="button"
                onClick={handlePay}
                disabled={isPaying || !invoice.amount}
                className="relative w-full h-14 rounded-xl text-white text-lg font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 transition-colors duration-200 shadow-[0_8px_20px_rgba(22,163,74,0.28)]"
              >
                ይክፈሉ · Pay {formatAmount(invoice.amount)} ብር
                {isPaying && (
                  <span className="absolute inset-0 rounded-xl bg-black/40 flex items-center justify-center">
                    <span className="w-6 h-6 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                  </span>
                )}
              </button>

              {payError && (
                <p className="mt-3 text-sm text-center text-red-600">{payError}</p>
              )}

              <div className="mt-4 flex items-center justify-center gap-1.5 text-[#6B5B95] text-xs">
                <LockIcon styles="w-3.5 h-3.5" />
                <span>Secure checkout · Telebirr, CBE Birr እና ሌላ ቀላል የክፍያ አማራጮች</span>
              </div>
              <div className="mt-3 flex items-center justify-center gap-4">
                <img src={teleBirrLogo} alt="Telebirr" className="h-7" />
                <img src={cbeBirrLogo} alt="CBE Birr" className="h-7" />
              </div>
            </div>
          </>
        )}

        <footer className="mt-auto pt-8 flex flex-col items-center gap-2 text-center text-sm text-[#6B5B95]">
          <p>ይህ የiChereta ኦፊሴላዊ የክፍያ ገጽ ነው።</p>
          <a
            href="tel:+251955305555"
            className="inline-flex items-center gap-1.5 font-semibold text-[#664BC7]"
          >
            <PhoneIcon styles="w-4 h-4" />
            0955305555
          </a>
          <a
            href="https://t.me/iCheretaSupport"
            target="_blank"
            rel="noreferrer"
            className="text-[#664BC7]"
          >
            Telegram support
          </a>
        </footer>
      </main>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 py-3">
    <dt className="text-sm text-[#7A6B9E]">{label}</dt>
    <dd className="text-sm font-semibold text-[#2D2156] text-right">{value}</dd>
  </div>
);

const PaidInvoiceCard = ({ invoice }) => (
  <section className="bg-white rounded-2xl shadow-[0_10px_30px_rgba(61,40,120,0.08)] px-5 pt-6 pb-7 flex flex-col items-center text-center">
    <CheckCircleIcon styles="w-14 h-14 text-green-500" />
    <h2 className="mt-4 text-xl font-bold text-[#2D2156]">
      ይህ ክፍያ ተፈጽሟል
    </h2>
    <p className="mt-2 text-sm text-[#6B5B95] max-w-[34ch]">
      This invoice has already been paid. Your iChereta subscription is active.
    </p>

    <div className="mt-5 w-full rounded-xl bg-[#F6F3FC] px-4 py-3 text-left">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-[#7A6B9E]">Invoice</span>
        <span className="text-sm font-semibold text-[#2D2156]">
          #{invoice.invoice_number}
        </span>
      </div>
      {invoice.amount > 0 && (
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-xs text-[#7A6B9E]">Amount</span>
          <span className="text-sm font-semibold text-[#2D2156]">
            {formatAmount(invoice.amount)} ብር
          </span>
        </div>
      )}
      {invoice.plan_name && (
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-xs text-[#7A6B9E]">Plan</span>
          <span className="text-sm font-semibold text-[#2D2156]">
            {invoice.plan_name}
          </span>
        </div>
      )}
    </div>

    <div className="mt-6 w-full rounded-2xl bg-[#664BC7] px-4 pt-5 pb-4 text-white">
      <p className="text-lg font-bold">የiChereta መተግበሪያ ያውርዱ</p>
      <p className="mt-1 text-sm text-white/85">
        ጨረታዎችን በቀላሉ በስልክዎ ይመልከቱ። Download the app and start using iChereta.
      </p>
      <div className="mt-4 flex flex-col gap-3">
        <a
          href="https://play.google.com/store/apps/details?id=com.ichereta.app"
          target="_blank"
          rel="noreferrer"
          className="w-full rounded-xl bg-white py-3 flex items-center justify-center"
        >
          <img
            src={googlePlayLogo}
            alt="Get it on Google Play"
            className="h-14 w-auto"
          />
        </a>
        <a
          href="https://apps.apple.com/us/app/ichereta/id6502936517"
          target="_blank"
          rel="noreferrer"
          className="w-full rounded-xl bg-white py-3 flex items-center justify-center"
        >
          <img
            src={appStoreLogo}
            alt="Download on the App Store"
            className="h-14 w-auto"
          />
        </a>
      </div>
    </div>

    <a
      href="https://ichereta.com/login"
      className="mt-4 text-sm font-semibold text-[#664BC7]"
    >
      ወይም ወደ iChereta.com ይግቡ · Log in
    </a>
  </section>
);

const StatusCard = ({
  icon,
  title,
  subtitle,
  invoiceNumber,
  amount,
  actionLabel,
  actionHref,
  onAction,
}) => (
  <section className="bg-white rounded-2xl shadow-[0_10px_30px_rgba(61,40,120,0.08)] px-5 py-8 flex flex-col items-center text-center">
    {icon}
    <h2 className="mt-4 text-xl font-bold text-[#2D2156]">{title}</h2>
    <p className="mt-2 text-sm text-[#6B5B95] max-w-[32ch]">{subtitle}</p>
    {invoiceNumber && (
      <p className="mt-4 text-sm font-semibold text-[#2D2156]">
        Invoice #{invoiceNumber}
      </p>
    )}
    {amount > 0 && (
      <p className="mt-1 text-sm text-[#6B5B95]">
        {formatAmount(amount)} ብር
      </p>
    )}
    {actionHref && (
      <a
        href={actionHref}
        className="mt-6 w-full h-12 rounded-xl bg-[#664BC7] text-white font-semibold flex items-center justify-center"
      >
        {actionLabel}
      </a>
    )}
    {onAction && (
      <button
        type="button"
        onClick={onAction}
        className="mt-6 w-full h-12 rounded-xl bg-[#664BC7] text-white font-semibold"
      >
        {actionLabel}
      </button>
    )}
  </section>
);

const LoadingCard = () => (
  <section className="bg-white rounded-2xl shadow-[0_10px_30px_rgba(61,40,120,0.08)] px-5 py-6 animate-pulse">
    <div className="h-6 w-24 rounded-full bg-[#EFEAF8]" />
    <div className="mt-5 h-3 w-28 rounded bg-[#EFEAF8]" />
    <div className="mt-2 h-6 w-40 rounded bg-[#EFEAF8]" />
    <div className="mt-6 h-3 w-24 rounded bg-[#EFEAF8]" />
    <div className="mt-2 h-10 w-48 rounded bg-[#EFEAF8]" />
    <div className="mt-6 space-y-3">
      <div className="h-5 w-full rounded bg-[#EFEAF8]" />
      <div className="h-5 w-full rounded bg-[#EFEAF8]" />
    </div>
    <div className="mt-8 h-14 w-full rounded-xl bg-[#EFEAF8]" />
  </section>
);

export default GuestPay;
